<template>
  <VDialogV3
    id="transport-route-card"
    position="right"
    width="600px"
    height="100vh"
    :entity="localTransportRoute"
    :context-menu="TransportRoutesService.contextMenu"
    :submit-method="submit"
  >
    <section>
      <VSelect
        v-model="localTransportRoute.zoneFrom"
        label="Откуда"
        layout="vertical"
        required
        data-url="transportZones.getList"
      />

      <VSelect
        v-model="localTransportRoute.zoneTo"
        label="Куда"
        layout="vertical"
        required
        data-url="transportZones.getList"
      />

      <VSelectV2
        v-model="localTransportRoute.deliverer"
        label="Перевозчик"
        layout="vertical"
        :data="({ pattern }) => PartnersService.getList({ pattern, types: ['deliverer'] })"
      />

      <VCheckboxV2
        v-model="localTransportRoute.isMain"
        :disabled="isMainInitially === true"
      >
        Основной перевозчик пары зон
      </VCheckboxV2>
      <div class="description">
        По основному перевозчику считается доставка. Снять признак можно, только назначив основным другой маршрут этой пары зон
      </div>
    </section>

    <hr>

    <section>
      <VInputNumberRusV2
        v-model="localTransportRoute.costPerKg"
        :type="Price"
        layout="vertical"
        :required="localTransportRoute.hasTariffs === false"
        :disabled="localTransportRoute.hasTariffs === true"
        disabled-reasons="Заполнены тарифы перевозчика: доставка считается по ним, а не по общей цене"
        label="Цена за кг"
        :unit="localTransportRoute.costCurrency?.toString({ format: 'symbol' })"
      />

      <VInputNumberRusV2
        v-model="localTransportRoute.costPerM3"
        :type="Price"
        layout="vertical"
        :required="localTransportRoute.hasTariffs === false"
        :disabled="localTransportRoute.hasTariffs === true"
        disabled-reasons="Заполнены тарифы перевозчика: доставка считается по ним, а не по общей цене"
        label="Цена за м³"
        :unit="localTransportRoute.costCurrency?.toString({ format: 'symbol' })"
      />

      <VSelect
        v-model="localTransportRoute.costCurrency"
        layout="vertical"
        label="Валюта"
        disabled
        required
        option-label="symbol"
        option-description="description"
        :options="CurrenciesService.currencies"
      />
    </section>

    <hr>

    <section>
      <VSelectV2
        v-model="localTransportRoute.vehicleType"
        layout="vertical"
        label="Тип транспорта"
        :data="vehicleTypesSource"
        :disabled="localTransportRoute.hasTariffs === true"
        disabled-reasons="Заполнены тарифы перевозчика: маршрут считается по сетке, а не рейсом целой машиной"
      />

      <VSelectV2
        :disabled="!localTransportRoute.vehicleType || localTransportRoute.hasTariffs === true"
        :disabled-reasons="localTransportRoute.hasTariffs === true ? 'Заполнены тарифы перевозчика: маршрут считается по сетке, а не рейсом целой машиной' : null"
        v-model="localTransportRoute.vehicle"
        layout="vertical"
        label="Транспорт"
        :data="vehiclesSource"
      />
      <VInputNumberRusV2
        v-model="localTransportRoute.ftlCost"
        :type="Price"
        layout="vertical"
        label="Ставка за рейс"
        :disabled="localTransportRoute.hasTariffs === true"
        disabled-reasons="Заполнены тарифы перевозчика: маршрут считается по сетке, а не рейсом целой машиной"
        :unit="localTransportRoute.costCurrency?.toString({ format: 'symbol' })">
        <template #helper-text>
          Перевозка целым грузовиком, выделенным под груз. Стоимость фиксированная, независимо от веса/объёма.
        </template>
      </VInputNumberRusV2>
    </section>

    <hr>

    <section id="transport-route-tariffs">
      <header>
        Тарифы перевозчика
        <VLink
          material="update"
          :disabled="localTransportRoute.isFtl === true || localTransportRoute.hasTariffs === false"
          @click="createTariffGrid"
        > Обновить все тарифы </VLink>
      </header>

      <div v-if="localTransportRoute.hasTariffs === true" class="description">
        Обновление — новые тарифы с сегодняшнего дня с теми же весом, объёмом и плотностью: введите стоимости из нового прайса и при необходимости поправьте дату начала, прежние тарифы сохранятся
      </div>

      <div v-if="localTransportRoute.isFtl === true" class="description">
        Заполнена перевозка целой машиной: маршрут считается рейсом, а не по сетке
      </div>

      <nav v-if="localTransportRoute.tariffStartDates.length > 0">
        <template v-for="start of localTransportRoute.tariffStartDates" :key="start.toDateString()">
          <span v-if="isSelectedTariffStart(start) === true">тарифы с {{ start.toString() }}</span>
          <VLink v-else @click="selectedTariffStart = start">тарифы с {{ start.toString() }}</VLink>
          <span v-if="isActualTariffStart(start) === true" class="description"> действует</span>
        </template>
      </nav>

      <template v-if="selectedTariffStart !== null">
        <VDateInput
          :model-value="selectedTariffStart"
          label="Действует с"
          required
          :disabled="localTransportRoute.isFtl === true"
          disabled-reasons="Заполнена перевозка целой машиной: маршрут считается рейсом, а не по сетке"
          @update:model-value="setTariffGridStart"
        />

        <VDateInput
          :model-value="selectedTariffGridEnd"
          label="Действует по"
          :disabled="localTransportRoute.isFtl === true"
          disabled-reasons="Заполнена перевозка целой машиной: маршрут считается рейсом, а не по сетке"
          @update:model-value="setTariffGridEnd"
        />

        <VDataGridV2
          :data="localTransportRoute.getTariffsByStart(selectedTariffStart)"
          entity-type="transport-route-tariff"
          :use-filters-cache="false"
        >
          <VDataGridColumnV2 id="weight" label="Вес" width="120px" content-class="form">
            <template #default="{ row }">
              <VInputNumberRusV2 v-model="row.weight" :type="Weight" unit="кг" autocomplete="off" :disabled="localTransportRoute.isFtl === true" disabled-reasons="Заполнена перевозка целой машиной: маршрут считается рейсом, а не по сетке" />
            </template>
          </VDataGridColumnV2>

          <VDataGridColumnV2 id="volume" label="Объём" width="120px" content-class="form">
            <template #default="{ row }">
              <VInputNumberRusV2 v-model="row.volume" :type="Volume" unit="м³" autocomplete="off" :disabled="localTransportRoute.isFtl === true" disabled-reasons="Заполнена перевозка целой машиной: маршрут считается рейсом, а не по сетке" />
            </template>
          </VDataGridColumnV2>

          <VDataGridColumnV2 id="density" label="Плотность" width="120px" content-class="form">
            <template #default="{ row }">
              <VInputNumberRusV2 v-model="row.density" :type="Density" unit="кг/м³" autocomplete="off" :disabled="localTransportRoute.isFtl === true" disabled-reasons="Заполнена перевозка целой машиной: маршрут считается рейсом, а не по сетке" />
            </template>
          </VDataGridColumnV2>

          <VDataGridColumnV2 id="cost" label="Стоимость" width="140px" content-class="form">
            <template #default="{ row }">
              <VInputNumberRusV2
                v-model="row.cost"
                :type="Price"
                required
                autocomplete="off"
                :disabled="localTransportRoute.isFtl === true"
                disabled-reasons="Заполнена перевозка целой машиной: маршрут считается рейсом, а не по сетке"
                :unit="localTransportRoute.costCurrency?.toString({ format: 'symbol' })"
              />
            </template>
          </VDataGridColumnV2>

          <VDataGridColumnV2 id="remove" width="40px">
            <template #default="{ row }">
              <VLink material="delete" :disabled="localTransportRoute.isFtl === true" @click="removeTariff(row)" />
            </template>
          </VDataGridColumnV2>
        </VDataGridV2>
      </template>

      <div v-else class="description">
        Прайс перевозчика не заполнен
      </div>

      <VLink material="add" :disabled="localTransportRoute.isFtl === true" @click="addTariff"> Добавить строку </VLink>
    </section>
  </VDialogV3>
</template>

<script lang="ts">

import TransportRoute            from '@/domains/TransportRoutes/TransportRoute';
import TransportRouteTariff      from '@/domains/TransportRoutes/TransportRouteTariff';
import PartnersService           from '@/domains/Partners/PartnersService';
import DateSimple                from '@/core/types/DateSimple';
import Weight                    from '@/core/types/Weight';
import Volume                    from '@/core/types/Volume';
import Density                   from '@/core/types/Density';
import TransportRoutesService    from '@/domains/TransportRoutes/TransportRoutesService';
import CurrenciesService         from '@/domains/Currencies/CurrenciesService';
import Price                     from '@/core/types/Price';

import VehicleTypesService       from "@/domains/VehicleTypes/VehicleTypesService";
import VehiclesService           from "@/domains/Vehicles/VehiclesService";
import type Vehicle from "@/domains/Vehicles/Vehicle";

export default defineComponent({
  name: 'TransportRouteCard',

  props: {
    /** ID маршрута — карточка сама загружает объект */
    id: {
      type:    Number,
      default: null,
    },
  },

  setup() {
    return {
      Price,
      Weight,
      Volume,
      Density,
      CurrenciesService,
      PartnersService,
      TransportRoutesService,
    };
  },

  data() {
    return {
      localTransportRoute: undefined as unknown as TransportRoute,

      /** Признак основного у сохранённого маршрута: снять его можно, только назначив основным другой маршрут */
      isMainInitially: false,

      /** Сетка, которая сейчас открыта на редактирование: строки с этой датой начала */
      selectedTariffStart: null as DateSimple | null,

      vehicleTypesSource: this.$core.loader.select(
        () => VehicleTypesService.getList().then(r => r.data),
      ).preloadOnMount(),

      vehiclesSource: this.$core.loader.select<Vehicle>(({ vehicleType }) =>
        vehicleType ? VehiclesService.getList({ vehicleTypeId: vehicleType.id }) : []
      )
        .dependsOn({ vehicleType: () => this.localTransportRoute.vehicleType })
        .preloadOnFocus()
    };
  },

  async mounted() {
    await this.init();
  },

  computed: {
    selectedTariffGridEnd(): DateSimple | null {
      return this.localTransportRoute.getTariffsByStart(this.selectedTariffStart!)[0]?.validityPeriod.end ?? null;
    },
  },

  methods: {
    async init(): Promise<void> {
      if (this.id) {
        this.localTransportRoute = await TransportRoutesService.get(this.id);
      }
      else {
        this.localTransportRoute = TransportRoute.fromNew();
      }

      this.isMainInitially = this.localTransportRoute.isMain;
      this.selectedTariffStart = this.localTransportRoute.actualTariffStart ?? this.localTransportRoute.tariffStartDates[0] ?? null;
    },

    isSelectedTariffStart(start: DateSimple): boolean {
      return this.selectedTariffStart?.equals(start) === true;
    },

    isActualTariffStart(start: DateSimple): boolean {
      return this.localTransportRoute.actualTariffStart?.equals(start) === true;
    },

    /** Обновление тарифов — пороги открытых тарифов с сегодняшнего дня без стоимостей: прайс перевозчика обычно меняется ценами,
     *  а пустая стоимость не даст тихо унести в расчёт старую цену */
    createTariffGrid(): void {
      const today = DateSimple.today();

      if (this.localTransportRoute.tariffStartDates.some(start => start.equals(today)) === false) {
        if (this.selectedTariffStart === null) {
          this.localTransportRoute.tariffs.push(TransportRouteTariff.fromStartDate(today));
        }
        else {
          this.localTransportRoute.getTariffsByStart(this.selectedTariffStart).forEach((tariff) => {
            this.localTransportRoute.tariffs.push(TransportRouteTariff.fromTariff(tariff, today));
          });
        }
      }

      this.selectedTariffStart = today;
      this.resetGeneralCost();
    },

    addTariff(): void {
      if (this.selectedTariffStart === null) {
        this.selectedTariffStart = DateSimple.today();
      }

      this.localTransportRoute.tariffs.push(TransportRouteTariff.fromStartDate(this.selectedTariffStart));
      this.resetGeneralCost();
    },

    /** С тарифами доставка считается только по ним: общую цену зануляем, чтобы не висела рядом и не сбивала с толку */
    resetGeneralCost(): void {
      this.localTransportRoute.costPerKg = null;
      this.localTransportRoute.costPerM3 = null;
    },

    removeTariff(tariff: TransportRouteTariff): void {
      this.localTransportRoute.tariffs.splice(this.localTransportRoute.tariffs.indexOf(tariff), 1);

      if (this.localTransportRoute.getTariffsByStart(this.selectedTariffStart!).length === 0) {
        this.selectedTariffStart = this.localTransportRoute.actualTariffStart ?? this.localTransportRoute.tariffStartDates[0] ?? null;
      }
    },

    /** Дата начала — общая для всей сетки: меняется у всех её строк сразу */
    setTariffGridStart(value: DateSimple | null): void {
      if (value !== null) {
        this.localTransportRoute.getTariffsByStart(this.selectedTariffStart!).forEach((tariff) => {
          tariff.validityPeriod.start = value;
        });

        this.selectedTariffStart = value;
      }
    },

    setTariffGridEnd(value: DateSimple | null): void {
      this.localTransportRoute.getTariffsByStart(this.selectedTariffStart!).forEach((tariff) => {
        tariff.validityPeriod.end = value;
      });
    },

    async submit(): Promise<boolean> {
      if (this.localTransportRoute.isExisting === true) {
        return await TransportRoutesService.update(this.localTransportRoute);
      }
      else {
        return await TransportRoutesService.add(this.localTransportRoute);
      }
    },
  },
});
</script>
